/** @jsxImportSource theme-ui */

import React, { useState } from "react"
import _ from "lodash"
import graphql from "babel-plugin-relay/macro"
import { Flex, Box } from "theme-ui"
import {
  useMutation
} from "react-relay"

import { faPlus  } from '@fortawesome/free-solid-svg-icons'

import TextField from "../Radiate/TextField/TextField"
import NewBoxButton from "../../components/Radiate/NewBoxButton/NewBoxButton"
import WebchatPreview from "../../components/WebchatPreview/WebchatPreview"
import Address from "./Address"
import InfoWithAlias from "./InfoWithAlias"

import * as Styles from "./BusinessInfoStyles"

const BusinessInfoMutation = graphql`
  mutation BusinessInfo_updateBusinessMutation ($input: UpdateBusinessInput!) {
    updateBusiness(input: $input) {
      business {
        id
        businessId
        profile {
          name
          legalName
          about
          phones {
            number
            alias
          }
          addresses {
            line1
            line2
            city
            state
            country
            alias
          }
          websites {
            url
            alias
          }
          emails {
            email
            alias
          }
          logoUrl
          botAgentImageUrl
        }
        chatConfiguration {
          themeColor
          customCss
          showAgentProfile
          localised {
            localeId
            persistentMenu {
              buttons
              enable
            }
            iceBreakers {
              ctas
              enable
            }
          }
        }
      }
    }
  }
`
const BusinessInfo = ({
  info,
}) => {
  const [ businessInfo, setBusinessInfo ] = useState(info)

  const [commit, isInFlight] = useMutation(
    BusinessInfoMutation
  )

  return (
    <Flex>
      <Box sx={{ flexGrow: 1, height: "608px", width: "calc(100% - 440px)", minWidth: "500px" }}>
        <Flex py={3} pr={4} sx={{ justifyContent: "flex-end" }}>
          <NewBoxButton
            text="Save"
            loading={isInFlight}
            primary
            disabled={_.isEqual(businessInfo, info)}
            onClick={() => {
              commit({
                variables: {
                  input: {
                    businessId: info?.businessId,
                    profile: businessInfo?.profile
                  },
                },
                onCompleted: (response, errs) => {},
                onError: (err) => {
                  console.log(err)
                }
              })
            }}
          />
        </Flex>
        <Flex pr={4} sx={{ width: "100%", height: "calc(100% - 66px)", overflow: "scroll" }}>
          <Box sx={{ flexGrow: 1 }}>
            <Box pb={2}>
              <TextField
                label="Logo URL"
                borderStyle
                useInput
                value={businessInfo?.profile?.logoUrl}
                onChange={(e) => {
                  const modBusinessInfo = _.cloneDeep(businessInfo)
                  modBusinessInfo.profile.logoUrl = e.target.value
                  setBusinessInfo(modBusinessInfo)
                }}
              />
            </Box>
            <Box pb={2}>
              <TextField
                label="Bot Agent Image URL"
                borderStyle
                useInput
                value={businessInfo?.profile?.botAgentImageUrl}
                onChange={(e) => {
                  const modBusinessInfo = _.cloneDeep(businessInfo)
                  modBusinessInfo.profile.botAgentImageUrl = e.target.value
                  setBusinessInfo(modBusinessInfo)
                }}
              />
            </Box>
            <Box pb={2}>
              <TextField
                label="Name"
                borderStyle
                useInput
                value={businessInfo?.profile?.name}
                onChange={(e) => {
                  const modBusinessInfo = _.cloneDeep(businessInfo)
                  modBusinessInfo.profile.name = e.target.value
                  setBusinessInfo(modBusinessInfo)
                }}
              />
            </Box>
            <Box pb={2}>
              <TextField
                label="Legal Name"
                borderStyle
                useInput
                value={businessInfo?.profile?.legalName}
                onChange={(e) => {
                  const modBusinessInfo = _.cloneDeep(businessInfo)
                  modBusinessInfo.profile.legalName = e.target.value
                  setBusinessInfo(modBusinessInfo)
                }}
              />
            </Box>
            <Box pb={2}>
              <TextField
                label="About"
                borderStyle
                value={businessInfo?.profile?.about}
                onChange={(e) => {
                  const modBusinessInfo = _.cloneDeep(businessInfo)
                  modBusinessInfo.profile.about = e.target.value
                  setBusinessInfo(modBusinessInfo)
                }}
              />
            </Box>
            <Box pb={2}>
              <Styles.Label>
                <span>Address</span>
                <NewBoxButton
                  text="Address"
                  icon={faPlus}
                  primary
                  inverted
                  onClick={() => {
                    const modBusinessInfo = _.cloneDeep(businessInfo)
                    const newAddress = {
                      alias: "",
                      line1: "",
                      line2: "",
                      city: "",
                      state: "",
                      zip: "",
                      country: ""
                    }
                    let curAddresses = []
                    if (modBusinessInfo?.profile?.addresses) {
                      curAddresses = modBusinessInfo?.profile?.addresses
                    }
                    const modAddresses = [
                      ...curAddresses,
                      newAddress
                    ]
                    modBusinessInfo.profile.addresses = modAddresses
                    setBusinessInfo(modBusinessInfo)
                  }}
                />
              </Styles.Label>
              <Styles.InfoWrapper>
                {(businessInfo?.profile?.addresses || []).map((address, index) => {
                  return (
                    <Address
                      businessInfo={businessInfo}
                      index={index}
                      address={address}
                      onChangeAddress={(value) => {
                        setBusinessInfo(value)
                      }}
                    />
                  )
                })}
              </Styles.InfoWrapper>
            </Box>
            <Box pb={2}>
              <Styles.Label>
                <span>Phones</span>
                <NewBoxButton
                  text="Phone"
                  icon={faPlus}
                  primary
                  inverted
                  onClick={() => {
                    const modBusinessInfo = _.cloneDeep(businessInfo)
                    const newPhones = {
                      alias: "",
                      number: ""
                    }
                    let curPhones = []
                    if (modBusinessInfo?.profile?.phones) {
                      curPhones = modBusinessInfo?.profile?.phones
                    }
                    const modPhones = [
                      ...curPhones,
                      newPhones
                    ]
                    modBusinessInfo.profile.phones = modPhones
                    setBusinessInfo(modBusinessInfo)
                  }}
                />
              </Styles.Label>
              <Styles.InfoWrapper>
                {(businessInfo?.profile?.phones || []).map((phone, index) => {
                  return (
                    <InfoWithAlias
                      businessInfo={businessInfo}
                      index={index}
                      phone={phone}
                      key1="phones"
                      key2="number"
                      onChangeInfo={(value) => {
                        setBusinessInfo(value)
                      }}
                    />
                  )
                })}
              </Styles.InfoWrapper>
            </Box>
            <Box pb={2}>
              <Styles.Label>
                <span>Emails</span>
                <NewBoxButton
                  text="Email"
                  icon={faPlus}
                  primary
                  inverted
                  onClick={() => {
                    const modBusinessInfo = _.cloneDeep(businessInfo)
                    const newEmail = {
                      alias: "",
                      email: ""
                    }
                    let curEmails = []
                    if (modBusinessInfo?.profile?.emails) {
                      curEmails = modBusinessInfo?.profile?.emails
                    }
                    const modEmails = [
                      ...curEmails,
                      newEmail
                    ]
                    modBusinessInfo.profile.emails = modEmails
                    setBusinessInfo(modBusinessInfo)
                  }}
                />
              </Styles.Label>
              <Styles.InfoWrapper>
                {(businessInfo?.profile?.emails || []).map((email, index) => {
                  return (
                    <InfoWithAlias
                      businessInfo={businessInfo}
                      index={index}
                      email={email}
                      key1="emails"
                      key2="email"
                      onChangeInfo={(value) => {
                        setBusinessInfo(value)
                      }}
                    />
                  )
                })}
              </Styles.InfoWrapper>
              
            </Box>
            <Box pb={2}>
              <Styles.Label>
                <span>Websites</span>
                <NewBoxButton
                  text="Website"
                  icon={faPlus}
                  primary
                  inverted
                  onClick={() => {
                    const modBusinessInfo = _.cloneDeep(businessInfo)
                    const newWebsite = {
                      alias: "",
                      url: ""
                    }
                    let curWebsites = []
                    if (modBusinessInfo?.profile?.websites) {
                      curWebsites = modBusinessInfo?.profile?.websites
                    }
                    const modWebsites = [
                      ...curWebsites,
                      newWebsite
                    ]
                    modBusinessInfo.profile.websites = modWebsites
                    setBusinessInfo(modBusinessInfo)
                  }}
                />
              </Styles.Label>
              <Styles.InfoWrapper>
                {(businessInfo?.profile?.websites || []).map((website, index) => {
                  return (
                    <InfoWithAlias
                      businessInfo={businessInfo}
                      index={index}
                      website={website}
                      key1="websites"
                      key2="url"
                      onChangeInfo={(value) => {
                        setBusinessInfo(value)
                      }}
                    />
                  )
                })}
              </Styles.InfoWrapper>
            </Box>
          </Box>
        </Flex>
      </Box>
      <Box pt={2} sx={{ width: "440px", minWidth: "440px", minHeight: "608px", boxSizing: "border-box"}}>
        <WebchatPreview 
        info={businessInfo} 
        cssString={info?.chatConfiguration?.customCss || ""}
      />
      </Box>
    </Flex>
    
  )
}

export default BusinessInfo